<template>
	<PBlockUI
		:blocked="loading"
		fullScreen
	/>

	<!-- ************************************************************************** Data table -->
	<PCard>
		<template #title>
			<div class="flex align-items-center justify-content-between flex-wrap gap-2">
				<WTitle text="Tariff Calculations" />
				<PTag
					:value="'Read-only'"
					icon="pi pi-lock"
					severity="success"
					v-tooltip.left="`Tariff formulas are fixed in the deployed build and cannot be edited`"
				/>
			</div>
		</template>
		<template #content>
			<!-- ****************************************** Formula-set governance banner -->
			<div class="formula-set-banner">
				<div class="formula-set-status">
					<i class="pi pi-lock"></i>
					<div class="flex flex-column">
						<span class="formula-set-status__label">Tariff formulas are built-in</span>
						<span class="formula-set-status__hint">
							Configured at commissioning to match the signed PPA, and frozen for the life of the project.
						</span>
					</div>
				</div>
				<div class="formula-set-meta">
					<div class="formula-set-meta__item">
						<span class="formula-set-meta__key">Version</span>
						<span class="formula-set-meta__val">v{{ formulaSet.version || '—' }}</span>
					</div>
					<div class="formula-set-meta__item">
						<span class="formula-set-meta__key">Checksum</span>
						<span class="formula-set-meta__val mono">{{ formulaSet.checksum || '—' }}</span>
					</div>
					<div class="formula-set-meta__item">
						<span class="formula-set-meta__key">Formulas</span>
						<span class="formula-set-meta__val">{{ formulaSet.count || list.length }}</span>
					</div>
				</div>
			</div>

			<PDataTable
				:alwaysShowPaginator="false"
				:highlightOnSelect="true"
				:loading="loading"
				:resizableColumns="true"
				:rowHover="true"
				:rows="100"
				:showGridlines="true"
				:sortOrder="1"
				:stripedRows="true"
				:value="list"
				class="formula-table"
				data-key="id"
				paginator
				size="small"
			>
				<template #empty>No records found.</template>

				<PColumn
					bodyClass="text-left"
					class="justify-content-center"
					field="tag"
					header="Tag Name"
					headerClass="py-3"
					sortable
					style="width: 140px;"
				>
					<template #body="{ data }">
						<span class="formula-tag">
							<template v-if="data.tagParts">
								{{ data.tagParts.base }}<sub v-if="data.tagParts.sub">{{ data.tagParts.sub }}</sub><sup v-if="data.tagParts.sup">{{ data.tagParts.sup }}</sup>
							</template>
							<template v-else>{{ data.tag }}</template>
						</span>
					</template>
				</PColumn>
				<PColumn
					bodyClass="text-left text-wrap"
					class="justify-content-center"
					field="formulaForDataTable"
					header="Formula"
					headerClass="py-3"
				>
					<template #body="{ data }">
						<div class="formula-expression">
							<span
								:class="['formula-token', tokenClass(token)]"
								:key="index"
								v-for="(token, index) in data.formula"
							>
								<template v-if="token.kind === 'symbol'">
									{{ token.base }}<sub v-if="token.sub">{{ token.sub }}</sub><sup v-if="token.sup">{{ token.sup }}</sup>
								</template>
								<template v-else>{{ token.text }}</template>
							</span>
						</div>
					</template>
				</PColumn>
				<PColumn
					bodyClass="text-left"
					class="justify-content-center"
					field="unit"
					header="Unit"
					headerClass="py-3"
					style="width: 110px;"
				></PColumn>
				<PColumn
					bodyClass="text-left text-wrap"
					field="description"
					header="Description"
					headerClass="py-3"
				></PColumn>
			</PDataTable>
			<!-- lower buttons bar -->
			<div
				class="flex justify-content-between align-items-center"
				v-if="appIsInLiveMode"
			>
				<!-- Generate invoice button -->
				<PButton
					@click="doTariffCalculations()"
					class="mt-3"
					label="Generate Invoice"
					size="small"
				></PButton>
				<!-- Spinner -->
				<div
					class="flex align-items-center"
					v-if="loading"
				>
					Generating invoice
					<PProgressSpinner
						animationDuration="0.5s"
						class="ml-2"
						strokeWidth="6"
						style="width: 25px; height: 25px"
					/>
				</div>
				<!-- Invoice download link -->
				<div
					class="flex align-items-center"
					v-if="!loading && invoiceFilename"
				>
					<PButton
						@click="downloadInvoice()"
						class="mt-3"
						label="Save Invoice"
						size="small"
						text
					></PButton>
				</div>
			</div>
		</template>
	</PCard>
</template>

<script lang="ts">
	import { defineComponent } from 'vue'
	import BEBroker from '@/brokers/BEBroker'
	import Utils from '@/utils/Utils'
	import Constants from '@/helpers/Constants'
	import store from "@/store"

	export default defineComponent({
		name: 'FormulasView',
		components: {
		},

		data() {
			return {
				utils: Utils,
				component: 'formulas',
				loading: true,
				list: [] as Array<any>,
				invoiceFilename: '',
				formulaSet: {
					version: '',
					checksum: '',
					count: 0,
				},
			}
		},

		computed: {
			appIsInLiveMode() {
				return store.getters.getAppMode === 'Live' ? true : false
			},
		},

		async mounted() {
			Utils.generatePid(Constants.OPID.READ)
			await this.refreshList()
			await this.refreshFormulaSet()
			this.loading = false
		},

		methods: {
			/**
			 * Refresh the (read-only, code-defined) formulas list.
			 */
			async refreshList() {
				const res = await BEBroker.get(this.component)
				if (res != null && Object.keys(res).length !== 0) {
					this.list = res.map((item: any) => {
						return {
							...item,
							tagParts: this.parseSymbol(String(item.tag || '')),
							formula: this.parseFormula(String(item.formula || '')),
							formulaForDataTable: item.formula,
						}
					})
				} else {
					this.list = []
				}
			},


			/**
			 * Refresh the formula-set fingerprint (version / checksum / count).
			 */
			async refreshFormulaSet() {
				try {
					const res = await BEBroker.getFormulaSetStatus()
					if (res && Object.keys(res).length !== 0) {
						this.formulaSet = {
							version: res.version || '',
							checksum: res.checksum || '',
							count: res.count || 0,
						}
					}
				} catch (e) {
					// Non-critical for viewing the formulas.
				}
			},


			/**
			 * Split a catalog symbol into base + subscript (+ optional superscript).
			 * Matches PPA-style tags: CCRn → CCRₙ, NEEi,act → NEEᵢ,act, A1 → A₁.
			 */
			parseSymbol(raw: string): { base: string; sub?: string; sup?: string } | null {
				const t = String(raw || '').trim()
				if (!t || /^-?\d+(\.\d+)?$/.test(t)) return null
				if (t === 'MAX' || t === 'MIN') return null
				if (['+', '-', '*', '/', '(', ')'].includes(t)) return null

				// Acronym/base ending in an uppercase letter, then index n|i|r|digits,
				// optional ,qualifier — e.g. CCRn, NEEi,deemed-b, A1. Avoids treating
				// trailing letters in long names (AvailabilityDeclaration) as indices.
				const m = t.match(/^([A-Za-z]*[A-Z])([nir]|\d+)(?:,([\w-]+))?$/)
				if (!m) return { base: t }

				const base = m[1]
				const index = m[2]
				const qualifier = m[3]
				const sub = qualifier ? `${index},${qualifier}` : index
				return { base, sub }
			},


			/**
			 * Tokenize a canonical formula string into renderable parts, collapsing
			 * adjacent identical multiplications (X * X) into a single X² term.
			 */
			parseFormula(formula: string): Array<Record<string, any>> {
				const raw = String(formula || '').split(/\s+/).filter((t) => t.length > 0)
				const out: Array<Record<string, any>> = []
				let i = 0
				while (i < raw.length) {
					const cur = raw[i]
					const sym = this.parseSymbol(cur)
					if (
						sym &&
						i + 2 < raw.length &&
						raw[i + 1] === '*' &&
						raw[i + 2] === cur
					) {
						out.push({ kind: 'symbol', base: sym.base, sub: sym.sub, sup: '2', raw: cur })
						i += 3
						continue
					}
					if (sym) {
						out.push({ kind: 'symbol', base: sym.base, sub: sym.sub, sup: sym.sup, raw: cur })
					} else {
						out.push({ kind: 'text', text: cur, raw: cur })
					}
					i += 1
				}
				return out
			},


			/**
			 * Classify a formula token for professional syntax highlighting.
			 */
			tokenClass(token: Record<string, any>): string {
				const t = String(token?.raw ?? token?.text ?? '').trim()
				if (t === 'MAX' || t === 'MIN') return 'formula-token--keyword'
				if (t === '(' || t === ')') return 'formula-token--paren'
				if (['+', '-', '*', '/'].includes(t)) return 'formula-token--operator'
				if (/^-?\d+(\.\d+)?$/.test(t)) return 'formula-token--number'
				return 'formula-token--variable'
			},


			/**
			 * Perform tariff calculations and generate the invoice
			 */
			async doTariffCalculations() {
				Utils.showWarningMessage('No data available to perform the tariff calculations.')
			},


			/**
			 * Download the invoice
			 */
			downloadInvoice() {
				window.open(`${Constants.SERVER_URL}/download_invoice?comp=invoice&filename=${this.invoiceFilename}`)
			}

		}
	})
</script>

<style scoped>
/* ******************************************* Formula-set governance banner */
.formula-set-banner {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 1rem;
	padding: 0.85rem 1.1rem;
	margin-bottom: 1rem;
	border-radius: 10px;
	border: 1px solid var(--surface-border);
	border-left: 4px solid var(--green-500);
	background: var(--surface-50);
}

.formula-set-status {
	display: flex;
	align-items: center;
	gap: 0.75rem;
}

.formula-set-status i {
	font-size: 1.35rem;
	color: var(--green-600);
}

.formula-set-status__label {
	font-weight: 600;
	font-size: 0.95rem;
}

.formula-set-status__hint {
	font-size: 0.78rem;
	color: var(--text-color-secondary);
}

.formula-set-meta {
	display: flex;
	align-items: stretch;
	gap: 1.75rem;
}

.formula-set-meta__item {
	display: flex;
	flex-direction: column;
	gap: 0.15rem;
}

.formula-set-meta__key {
	font-size: 0.68rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: var(--text-color-secondary);
}

.formula-set-meta__val {
	font-weight: 600;
	font-size: 0.9rem;
}

.formula-set-meta__val.mono {
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	letter-spacing: 0.02em;
}

/* ******************************************* Professional formula rendering */
.formula-tag {
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	font-weight: 600;
	color: var(--primary-color);
}

.formula-expression {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.28rem;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	font-size: 0.85rem;
	line-height: 1.6;
}

.formula-token {
	white-space: nowrap;
}

.formula-tag sub,
.formula-tag sup,
.formula-token sub,
.formula-token sup {
	font-size: 0.72em;
	font-weight: 600;
	line-height: 0;
}

.formula-tag sub,
.formula-token sub {
	vertical-align: baseline;
	position: relative;
	bottom: -0.2em;
}

.formula-tag sup,
.formula-token sup {
	vertical-align: baseline;
	position: relative;
	top: -0.45em;
}

.formula-token--variable {
	color: var(--text-color);
	font-weight: 500;
}

.formula-token--operator {
	color: var(--pink-500);
	font-weight: 700;
	padding: 0 0.05rem;
}

.formula-token--paren {
	color: var(--text-color-secondary);
	font-weight: 700;
}

.formula-token--number {
	color: var(--orange-600);
}

.formula-token--keyword {
	color: var(--blue-700);
	background: var(--blue-50);
	border-radius: 4px;
	padding: 0 0.35rem;
	font-weight: 700;
}

.formula-table :deep(td) {
	vertical-align: middle;
}
</style>
